import { useCallback } from 'react';

import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { EntitySummary } from '@/lib/types';

import { useEntityAccessLayer } from '../../shared/hooks/use-entity-access-layer';
import { mapCharacterDTOToDomain } from '../api/mappers';
import type { CharacterFormValues } from '../schemas';
import type { Character } from '../types';

/**
 * Fields on the Character that are mirrored into the EntitySummary.
 * When any of these change, the summary is automatically updated.
 */
const CHARACTER_SUMMARY_FIELDS = [
  'name',
  'type',
  'shortDescription',
  'mainImageUrl',
  'mainImagePath',
] as const;

/**
 * Centralised access layer for characters.
 *
 * @example
 * ```tsx
 * const {
 *   characters,          // EntitySummary[]
 *   character,           // Character | null (if id provided)
 *   sections,            // SectionDefinition[]
 *   updateCharacter,     // (id, partial) => Promise<void>
 *   deleteCharacter,     // (id) => Promise<void>
 *   isLoading,
 *   error,
 * } = useCharacterAccessLayer(characterId);
 * ```
 */
export function useCharacterAccessLayer(characterId?: string) {
  const {
    summaries,
    item,
    sections,
    isLoading,
    error,
    updateItem,
    deleteItem,
    getNewId,
  } = useEntityAccessLayer<Character, EntitySummary>({
    entityType: 'characters',
    summaryFieldKeys: CHARACTER_SUMMARY_FIELDS,
    itemId: characterId,
    itemMapper: mapCharacterDTOToDomain,
  });

  const {
    saveItem,
    updateEntitySummary,
  } = useNovelRepository();

  /**
   * Creates a new character with initial data + summary.
   * NOTE: Image upload is NOT handled here — call this AFTER
   * images have been processed.
   */
  const createCharacter = useCallback(
    async (id: string, data: Omit<CharacterFormValues, 'pendingFiles'>) => {
      const now = new Date().toISOString();
      const newCharacter = {
        ...data,
        id,
        audit: { createdAt: now, updatedAt: now },
      };

      // Persist item
      await saveItem('entities', 'characters', newCharacter);

      // Extract summary fields with safe string coercion
      // (CharacterFormValues uses passthrough, so types are widened)
      const name = String(data.name ?? '');
      const type = String(data.type ?? '');
      const shortDescription = String(data.shortDescription ?? '');
      const mainImageUrl = String(data.mainImageUrl ?? '');
      const mainImagePath = String((data as Record<string, unknown>).mainImagePath ?? '');
      const primaryImageId = typeof data.primaryImageId === 'string' ? data.primaryImageId : null;

      // Write summary
      await updateEntitySummary('characters', id, {
        id,
        name,
        type,
        shortDescription,
        mainImageUrl,
        mainImagePath,
        primaryImageId,
        updatedAt: now,
      });
    },
    [saveItem, updateEntitySummary],
  );

  return {
    /** Character summary list, sorted by updatedAt DESC. */
    characters: summaries,
    /** Single character domain model (if ID provided). */
    character: item,
    /** Character UI settings (sections/fields). */
    sections,
    isLoading,
    error,

    /** Partial-update a character. Auto-syncs summary if needed. */
    updateCharacter: updateItem,

    /** Delete a character and its summary. */
    deleteCharacter: deleteItem,

    /** Create a new character (after image processing). */
    createCharacter,

    /** Generate a Firestore-compatible ID for a new character. */
    getNewId,
  };
}
